import { Component, OnInit, input, signal, effect, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { BehaviorBipInfoComponent } from '../../behaviors/behavior-bip-info/behavior-bip-info.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorBipInfoModel, PatientBehaviorAntecedentModel, PatientBehaviorConsequenceModel, PatientBehaviorReplacementModel, BehaviorFunctionModel, PatientBehaviorModel } from '../../../core/api/models';
import { StaffApiService } from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { finalize, forkJoin } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { Utils } from '../../../core/utils';


@Component({
  selector: 'app-bip-behaviors',
  standalone: true,
  imports: [CommonModule, SharedModule, BehaviorBipInfoComponent],
  templateUrl: './bip-behaviors.component.html',
  styleUrls: ['../../supervisor-main.component.css', './bip-behaviors.component.css']
})
export class BipBehaviorsComponent implements OnInit {
  patientID = input.required<string>();

  loading = signal<boolean>(false);
  behaviors = signal<PatientBehaviorModel[]>([]);
  startCurrentView: boolean = true;
  showDraftToggle = input<boolean>(true);
  scrollFragment = input<string | null>(null);
  useOfflineData = input<boolean>(false);
  canEditBip: boolean = false;
  savingAllDrafts = signal<boolean>(false);

  @ViewChildren(BehaviorBipInfoComponent) behaviorBipInfos!: QueryList<BehaviorBipInfoComponent>;

  constructor(private staffApi: StaffApiService, private alerts: AlertService, private route: ActivatedRoute, private router: Router, private offlineDataService: OfflineDataService, private authService: AuthService) {
    // React to external fragment input for staff modal context
    effect(() => {
      const frag = this.scrollFragment();
      if (frag) {
        this.tryScrollToFragment(frag, 0);
      }
    });
  }

  // Normalize optional input to a boolean defaulting to true
  shouldShowDraftToggle(): boolean {
    const value = this.showDraftToggle();
    return value ?? true;
  }

  ngOnInit(): void {
    this.canEditBip = this.authService.canEditBip();
    const useOffline = this.useOfflineData() || this.offlineDataService.isInOfflineMode();
    if (useOffline) {
      this.loadDataOffline();
    } else {
      this.loadData();
    }
    // Initialize view based on query param immediately
    const initialView = this.route.snapshot.queryParamMap.get('view');
    if (!this.canEditBip) {
      this.startCurrentView = true;
    } else if (initialView === 'draft') {
      this.startCurrentView = false;
    } else if (initialView === 'current') {
      this.startCurrentView = true;
    }
    // Attempt to scroll to fragment after navigation and as content becomes available
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      const fragment = this.route.snapshot.fragment;
      if (fragment) {
        this.tryScrollToFragment(fragment, 0);
      }
      // Also pick up the desired view from query params
      const view = this.route.snapshot.queryParamMap.get('view');
      if (!this.canEditBip) {
        this.startCurrentView = true;
      } else if (view === 'draft') {
        this.startCurrentView = false;
      } else if (view === 'current') {
        this.startCurrentView = true;
      }
    });
    // Also handle direct fragment changes (same route)
    this.route.fragment.subscribe(fragment => {
      if (fragment) {
        this.tryScrollToFragment(fragment, 0);
      }
    });
  }


  loadData() {
    this.loading.set(true);
    this.staffApi
    .GetPatientBehaviors({ patientID: Number(this.patientID()), onlyActive: true })
    .subscribe(
      (behaviors) => {
        this.loading.set(false);
        this.behaviors.set(behaviors);
      },
      (err) => {
        this.loading.set(false);
      }
    );
  }

  loadDataOffline() {
    this.loading.set(true);
    const data = this.offlineDataService.getOfflinePatientData(Number(this.patientID()));
    if (data && data.behaviors) {
      this.behaviors.set(data.behaviors);
    } else {
      this.behaviors.set([]);
    }
    this.loading.set(false);
  }

  private tryScrollToFragment(fragment: string, attempt: number) {
    const element = document.getElementById(fragment);
    if (element) {
      // Defer to ensure layout is settled
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }
    if (attempt < 60) { // retry up to ~6 seconds
      setTimeout(() => this.tryScrollToFragment(fragment, attempt + 1), 100);
    }
  }

  onGlobalViewChange(e: any) {
    if (!this.canEditBip) {
      this.startCurrentView = true;
      return;
    }
    if (this.behaviorBipInfos){
      this.behaviorBipInfos.forEach(c => c.onIsCurrentViewChange(e));
    }
    // const movingToCurrent = e?.value === true && this.startCurrentView === false;
    // // If switching to Current, silently save all drafts first
    // if (movingToCurrent && this.behaviorBipInfos) {
    //   const saves = this.behaviorBipInfos
    //     .map(c => c.getSaveObservable(false))
    //     .filter((o: any) => !!o);
    //   if ((saves as any).length) {
    //     forkJoin(saves as any).subscribe({ next: () => {}, error: () => {} });
    //   }
    // }
    this.startCurrentView = e.value;
  }

  saveAllDrafts() {
    if (this.savingAllDrafts()) {
      return;
    }
    if (!this.canEditBip) {
      this.alerts.info('You do not have permission to edit BIP drafts.');
      return;
    }
    if (this.startCurrentView) {
      this.alerts.info('Switch to Draft to save changes.');
      return;
    }
    if (!this.behaviorBipInfos) return;
    this.savingAllDrafts.set(true);
    const saves = this.behaviorBipInfos
      .filter(component => component.canEditBip())
      .map(component => component.getSaveObservable())
      .filter((save): save is NonNullable<typeof save> => save != null);
    if (!saves.length) {
      this.savingAllDrafts.set(false);
      return;
    }
    forkJoin(saves).pipe(
      finalize(() => this.savingAllDrafts.set(false))
    ).subscribe({
      next: () => {
        this.alerts.success('Saved');
      },
      error: error => this.alerts.error('Failed to save BIP behaviors - ' + Utils.getErrorMessageFromErrorResponse(error))
    });
  }
}


